import { useContext } from 'react'

import { getNavMenuContent } from '@hhgtech/hhg-components'
import {
  cleanUpUndefined,
  formatCategory,
  formatCategoryApi,
  formatHealthTool,
  formatRecentPost,
} from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { callApi, checkMobile, getApiPath } from 'api/utils'
import Component from 'container/categories/category'
import { useGetIsMobile } from 'hooks'
import { useCheckCloseBanner } from 'hooks/useCheckCloseBanner'
import { useScreenSize } from 'hooks/useScreenSize'
import {
  GetCategoryServerSideProps,
  ICategoryProps,
} from 'interfaces/categories.interface'
import { LOCALE_SPECS } from 'interfaces/constants'
import { Article, Category } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { State } from 'state/types'
import { cleanLink } from 'utils/htmlHelper'

export type Props = ICategoryProps

export const CategoryPageComponent = (props: ICategoryProps) => {
  const {
    navigators: _navigators,
    siteName,
    categoryInfo,
    relatedArticle,
    isMobileSsr,
  } = props

  const { paging } = relatedArticle
  const { state = {} as State } = useContext(AppContext)
  const { sitewideClose } = state
  const { isMobile: isMobileCsr } = useScreenSize()
  const isMobile = useGetIsMobile(isMobileSsr, isMobileCsr)
  // const { locale = 'vi-VN' } = useRouter()

  const closeSiteWideCookie = useCheckCloseBanner(
    'discover-disable-sitewide-category',
  )

  const navigators = {
    SEOData: {
      totalPages: paging?.maxNumPages,
      currentPage: paging?.currentPage,
      title: categoryInfo.categoryName + ' - ' + siteName,
      SEOImage: categoryInfo.featureImage || categoryInfo.iconUrl,
      apiMetaData: categoryInfo.metaTag,
      isIndexable: true,
      description: categoryInfo.description,
    },
    isSearchBarDisplay: isMobile,
    isSponsored: !!categoryInfo.isSponsored,
    sitewideBanner:
      closeSiteWideCookie || sitewideClose?.category
        ? null
        : _navigators.sitewideBanner,
    ..._navigators,
    // forcedCanonicalUrl:
    //   paging?.currentPage > 1
    //     ? `https://${domainLocales[locale]}${categoryInfo.permalink}?page=${paging.currentPage}`
    //     : '',
  }

  const pageProps: ICategoryProps = {
    ...props,
    isMobile,
    navigators,
  }

  return <Component {...pageProps} />
}

const findCategoryBySlug = (
  slugIndex: number,
  slugs: string[],
  categories: Category[],
  returnCategories: Category[],
) => {
  const category = categories.find(
    (c) => c.permalink.indexOf(`/${slugs[slugIndex]}`) > -1,
  )

  category && returnCategories.push(category)

  if (category?.childCates?.[0] && slugIndex < slugs.length - 1) {
    findCategoryBySlug(
      slugIndex + 1,
      slugs,
      category.childCates,
      returnCategories,
    )
  }
}

export const getServerSideProps: GetCategoryServerSideProps<ICategoryProps> = async (
  ctx,
  categoryRes,
) => {
  const { resolvedUrl, query, locale, req } = ctx

  const siteName = LOCALE_SPECS[locale || 'en-PH'].SITE_NAME_FORMATTED

  const {
    categoryInfo,
    subCategories,
    explainersPosts,
    community,
    sitewideBanner,
  } = formatCategoryApi(categoryRes)

  const userAgent = `${req.headers['user-agent']}`

  const isMobileSsr = checkMobile(userAgent)

  //#region validate
  if (!categoryInfo || !categoryRes || categoryRes.status !== 1) {
    return {
      notFound: true,
    }
  }

  const permalink = cleanLink(categoryInfo.permalink || '')
  const currentLink = cleanLink(resolvedUrl || '')

  if (currentLink.toLowerCase() !== permalink.toLowerCase()) {
    return {
      redirect: {
        destination: categoryInfo.permalink,
        permanent: true,
      },
    }
  }

  //#endregion

  const { page = 1 } = query
  const idExplainerPosts: string = explainersPosts
    ?.map((post: Article) => post.id)
    .toString()

  const categorySlugs = permalink.split('/').filter(Boolean)

  const relatedArticle = await formatRecentPost(
    await callApi(
      getApiPath(PATHS.GET_ARTICLE_OF_CATEGORY_NO_EXPLAINERS, {
        categoryId: categoryRes?.data?.category?.term_id,
        currentPage: +page || 1,
        ids: idExplainerPosts,
      }),
      'get',
    ),
  )

  // const relatedArticleRes = await Promise.all(arrRelatedArticles)

  // const relatedArticleRes = relatedArticleRes.reduce(
  //   (result, relatedArticles) => {
  //     if (!Object.keys(relatedArticles).length) return result

  //     const { articles, posts } = result

  //     return {
  //       articles: [...articles, ...relatedArticles.articles],
  //       posts: [...posts, ...relatedArticles.posts],
  //       paging: relatedArticles.paging,
  //     }
  //   },
  //   {
  //     articles: [],
  //     posts: [],
  //     paging: {
  //       currentPage: 0,
  //       perPage: 0,
  //       foundPosts: 0,
  //       maxNumPages: 0,
  //     },
  //   },
  // )

  const [
    topCategoriesApiRes,
    _healthTools,
    footer,
    navMenuContent,
  ] = await Promise.all([
    callApi(getApiPath(PATHS.GET_ALL_CATEGORIES), 'GET'),
    callApi(getApiPath(PATHS.GET_ALL_HEALTH_TOOLS), 'GET'),
    callApi(getApiPath(PATHS.GET_DYNAMIC_FOOTER), 'GET'),
    getNavMenuContent('vi-VN_MB'),
  ])

  const categories: Category[] =
    topCategoriesApiRes?.data?.map(formatCategory) ?? []

  const healthTools =
    _healthTools?.data && _healthTools?.data.length > 0
      ? _healthTools?.data?.map(formatHealthTool)
      : []

  const breadCrumbCategories: Category[] = []

  findCategoryBySlug(0, categorySlugs, categories, breadCrumbCategories)

  const props = {
    page: 'category',
    siteName,
    isMobileSsr,
    categories: [],
    categoryInfo: categoryInfo,
    subCategories: subCategories,
    explainersPosts: explainersPosts,
    categoryParent: breadCrumbCategories,
    relatedArticle,
    community: community || [],
    navigators: {
      categories,
      healthTools,
      togetherCommunities: [],
      footerMenu: footer?.data ?? {},
      sitewideBanner,
      dataNavMenuContent: cleanUpUndefined(navMenuContent) || null,
    },
  } as ICategoryProps

  return {
    props,
  }
}
